<template>
    <div class="autocomplete" style="height: max-content; position: absolute; display: contents;">

      <input v-model="query" type="text" placeholder='Buscar'/>

      <div class="dropdown-list">
        
        <template v-if="query.length==0">
          <p>{{beforeSearchMessage}}</p>
        </template>

        <template v-else-if="error">
          <p>{{emptySearchResultsMessage}}</p>
        </template>

        <template v-else-if="query.length!=0 && options.length==0">
          <p>{{searchingMessage}}</p>
        </template>

        <template v-else>
          <div
            class="dropdown-options"
            :class="{ 'dropdown-options--scrollable': reducedOptions.length > 2 }"
          >
            <div class="dropdown-option" v-for="o in reducedOptions" v-bind:key="o.index" @click="selectItem(o)">
                <div class="option-content">
                    <span class="option-name" :title="o[attribute]">{{o[attribute]}}</span>
                    <span v-if="o.category" class="option-category" :class="getCategoryClass(o.category)">
                        {{o.category}}
                    </span>
                </div>
            </div>
          </div>

          <div v-if="reducedOptions.length > 2" class="scroll-hint">
            <span aria-hidden="true">&darr;</span>
            Desplázate para ver más resultados
          </div>
        </template>

      </div>

  </div>
</template>

<style lang="scss" scoped>

$main-color:#004C98; //#7CD2A6;
$success-notification:#009D66;
$error-notification:#BF3714;
$secondary-color:#95989A;
$terciary-color:#37495F;

$disabled:#EBEAE4;
$dark-gray:#6A6C6B;

$background-color:lighten(#EDEDED,3.5%);
$background-light:#FCFCFC;
$background-medium:#E4E4E4;
$background-dark:darken(#BFBDBD,4.5%);
$background-darker:darken(#BFBDBD,4.5%);
$highlight-1:$main-color;
$highlight-2:#983824;
$text-color:darken(#95989A, 15%);
$light-color:#FFFFFF;
$button-color: $highlight-1;
$chart-primary:#689BCD;
$chart-primary-darker:#00559F;
$chart-secondary:#CBCBCB;

.autocomplete {
    position: relative;
    display: inline-block;
    width: 100%;

    .dropdown-list {
        display: none;
        position: absolute;
        background-color: #fff;
        width: 100%;
        box-shadow: 0px 2px 10px rgba(0,0,0,0.1);
        border-radius: 4px;
        padding: 0;
        z-index: 10;
        
        p {
            padding: 10px;
            margin: 0;
            color: $text-color;
            font-style: italic;
        }
        
        .dropdown-options {
            max-height: 128px;
            overflow-y: auto;
            overscroll-behavior: contain;
            scrollbar-gutter: stable;
            scrollbar-width: auto;
            scrollbar-color: #607d8b #edf1f4;

            &--scrollable {
                height: 128px;
                overflow-y: scroll;
            }

            &::-webkit-scrollbar {
                width: 11px;
            }

            &::-webkit-scrollbar-track {
                background: #edf1f4;
                border-radius: 0 4px 4px 0;
            }

            &::-webkit-scrollbar-thumb {
                background: #607d8b;
                border: 2px solid #edf1f4;
                border-radius: 8px;
            }

            &::-webkit-scrollbar-thumb:hover {
                background: #455a64;
            }
        }

        .dropdown-option {
            padding: 7px 10px;
            cursor: pointer;
            border-bottom: 1px solid #f0f0f0;
            
            &:last-child {
                border-bottom: none;
            }
            
            &:hover {
                background-color: #f5f8ff;
            }
            
            .option-content {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
            }
            
            .option-name {
                display: -webkit-box;
                font-weight: 500;
                color: #333;
                flex-grow: 1;
                overflow: hidden;
                font-size: 0.78rem;
                line-height: 1.35;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
            }
            
            .option-category {
                font-size: 0.68rem;
                padding: 2px 8px;
                border-radius: 12px;
                font-weight: 500;
                white-space: nowrap;
            }
            
            .category-ciudad {
                background-color: #e3f2fd;
                color: #1565c0;
            }
            
            .category-pais {
                background-color: #e8f5e9;
                color: #2e7d32;
            }
            
            .category-region {
                background-color: #fff3e0;
                color: #e65100;
            }
            
            .category-lugar {
                background-color: #f5f5f5;
                color: #616161;
            }
        }

        .scroll-hint {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 5px;
            min-height: 25px;
            padding: 4px 8px;
            background: #edf1f4;
            border-top: 1px solid #d9e0e5;
            color: #455a64;
            font-size: 0.68rem;
            font-weight: 600;
            line-height: 1.2;
        }
    }
    
    input {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 14px;
        
        &:focus {
            outline: none;
            border-color: $main-color;
            box-shadow: 0 0 0 2px rgba(0, 76, 152, 0.1);
        }
    }
    
    input:focus ~ .dropdown-list {
        display: block;
    }
    
    .dropdown-list:hover {
        display: block;
    }
}
</style>

<script>
export default{
  props: ['value','options', 'attribute', 'beforeSearchMessage', 'searchingMessage', 'emptySearchResultsMessage', 'error'],
  data(){
    return {
      query: this.value,
      // Search.vue ya limita la consulta a Nominatim a 10 resultados.
      // Se muestran todos dentro del dropdown con scroll vertical.
      limit: null,
    }
  },
  methods:{
      selectItem: function(item)
      {
          this.query = item[this.attribute];
          this.$emit('itemSelected', item);
      },
      setQuery: function(text) {
        this.query = text;
      },
      getCategoryClass: function(category) {
        category = category.toLowerCase();
        if (category.includes('ciudad')) return 'category-ciudad';
        if (category.includes('país')) return 'category-pais';
        if (category.includes('estado') || category.includes('región')) return 'category-region';
        return 'category-lugar';
      }

  },
  created:function(){

  },
  mounted:function(){
  },
  updated: function(){
      this.$emit('updated', this.query);
  },
  watch:{

  },
  computed:{
    reducedOptions(){
      return this.limit ? this.options.slice(0,this.limit) : this.options
    }
  }
}
</script>
